import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  Text,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Spinner,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { BASE_API_URL } from "../Components/config";
import axios from "axios";
import "./classroom-theme.css";
// Chakra modals render in a portal on <body>, outside the .stande-classroom
// wrapper, so we use the literal palette values rather than the CSS variables.
import { PALETTES } from "./classroomColors";

const CreateClassroom = ({ isOpen, onClose, isDark = true, onCreated }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;

  const [classroomName, setClassroomName] = useState("");
  const [boards, setBoards] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [, setBoardsError] = useState(null);
  const [, setQualificationsError] = useState(null);
  const [, setSubjectsError] = useState(null);

  const userGuid = localStorage.getItem("userGuid");
  const token = `Bearer ${localStorage.getItem("accessToken")}`;

  useEffect(() => {
    axios
      .get(`${BASE_API_URL}/api/v1/exam-boards/`)
      .then((response) => {
        setBoards(response.data);
        setBoardsError(null);
      })
      .catch((err) => {
        console.error(err);
        setBoardsError("There was an error loading your Boards.");
      });
  }, []);

  useEffect(() => {
    if (!selectedBoard?.guid) return;
    axios
      .get(`${BASE_API_URL}/api/v1/exam-qualifications/by-exam-board-guid/${selectedBoard.guid}`)
      .then((response) => {
        setQualifications(response.data);
        setQualificationsError(null);
      })
      .catch((err) => {
        console.error(err);
        setQualificationsError("There was an error loading your Qualifications");
      });
  }, [selectedBoard?.guid]);

  useEffect(() => {
    if (!selectedQualification) return;
    axios
      .get(`${BASE_API_URL}/api/v1/subjects/by-exam-qualification-guid/${selectedQualification.guid}`)
      .then((response) => {
        setSubjects(response.data);
        setSubjectsError(null);
      })
      .catch((err) => {
        console.error(err);
        setSubjectsError("There was an error loading your Subjects");
      });
  }, [selectedQualification?.guid]);

  const canCreate = Boolean(classroomName && selectedSubject?.guid) && !submitting;

  const resetForm = () => {
    setClassroomName("");
    setSelectedBoard(null);
    setSelectedQualification(null);
    setSelectedSubject(null);
    setQualifications([]);
    setSubjects([]);
  };

  const handleCreate = async () => {
    if (!userGuid || !classroomName || !selectedSubject?.guid) return;

    const payload = {
      name: classroomName,
      code: classroomName,
      subject_guid: selectedSubject.guid,
      instructor_guid: userGuid,
    };
    const headers = token ? { Authorization: token } : {};

    setSubmitting(true);
    try {
      await axios.post(`${BASE_API_URL}/api/v1/courses/`, payload, { headers });
      // Refresh the parent list so the new classroom appears without a reload.
      if (typeof onCreated === "function") await onCreated();
      resetForm();
      onClose?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const dropdownButtonProps = {
    as: Button,
    variant: "unstyled",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "42px",
    px: "14px",
    fontSize: "14px",
    fontWeight: 400,
    textAlign: "left",
    bg: p.inputBg,
    color: p.text,
    border: "1px solid",
    borderColor: p.inputBorder,
    borderRadius: "10px",
    rightIcon: <ChevronDownIcon color={p.textSub} />,
    _hover: { borderColor: p.accent },
    _active: { bg: p.inputBg },
  };

  const menuListProps = {
    bg: p.surface,
    color: p.text,
    border: "1px solid",
    borderColor: p.border,
    borderRadius: "10px",
    boxShadow: "0 12px 30px -12px rgba(0,0,0,0.4)",
    fontSize: "13px",
    py: "6px",
    maxHeight: "260px",
    overflowY: "auto",
  };

  const menuItemSx = {
    bg: "transparent",
    color: p.text,
    _hover: { bg: p.hover },
    _focus: { bg: p.hover },
  };

  const Field = ({ label, value, placeholder, items, onSelect, disabled }) => (
    <FormControl display="flex" flexDirection="column" gap="8px" flex="1" minW="150px">
      <FormLabel color={p.textSub} fontSize="13px" fontWeight="500" m={0}>
        {label}
      </FormLabel>
      <Menu>
        <MenuButton {...dropdownButtonProps} isDisabled={disabled} opacity={disabled ? 0.5 : 1}>
          {value || placeholder}
        </MenuButton>
        <MenuList {...menuListProps} className="stande-classroom" data-theme={isDark ? "dark" : "light"}>
          {items.map((it) => (
            <MenuItem key={it.id} sx={menuItemSx} onClick={() => onSelect(it)}>
              {`${it.code} | ${it.name}`}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </FormControl>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "2xl" }} isCentered>
      <ModalOverlay bg={p.overlay} backdropFilter="blur(2px)" />
      <ModalContent
        className="stande-classroom"
        data-theme={isDark ? "dark" : "light"}
        bg={p.surface}
        color={p.text}
        borderRadius="20px"
        border="1px solid"
        borderColor={p.border}
        boxShadow="0 24px 60px -20px rgba(0,0,0,0.5)"
        mx="16px"
      >
        <ModalCloseButton borderRadius="full" color={p.textSub} _hover={{ bg: p.hover }} />
        <ModalBody px={{ base: "24px", md: "36px" }} py="32px">
          <Flex flexDirection="column" gap="4px" mb="24px">
            <Text fontSize="22px" fontWeight="600" color={p.text} letterSpacing="-0.3px">
              Create a space
            </Text>
            <Text fontSize="14px" fontWeight="300" color={p.textSub}>
              Name your class and pick the exam board, qualification and subject.
            </Text>
          </Flex>

          <Flex flexDirection="column" gap="20px">
            <FormControl display="flex" flexDirection="column" gap="8px">
              <FormLabel color={p.textSub} fontSize="13px" fontWeight="500" m={0}>
                Classroom name
              </FormLabel>
              <Input
                value={classroomName}
                onChange={(e) => setClassroomName(e.target.value)}
                placeholder="e.g. Year 11 Biology"
                height="42px"
                borderRadius="10px"
                bg={p.inputBg}
                color={p.text}
                border="1px solid"
                borderColor={p.inputBorder}
                fontSize="14px"
                _placeholder={{ color: p.textSub }}
                _hover={{ borderColor: p.accent }}
                _focus={{ borderColor: p.accent, boxShadow: `0 0 0 1px ${p.accent}` }}
              />
            </FormControl>

            <Flex gap="14px" flexWrap="wrap">
              <Field
                label="Exam board"
                value={selectedBoard?.code}
                placeholder="Select board"
                items={boards}
                onSelect={(b) => {
                  setSelectedBoard(b);
                  setSelectedQualification(null);
                  setSelectedSubject(null);
                  setQualifications([]);
                  setSubjects([]);
                }}
              />
              <Field
                label="Qualification"
                value={selectedQualification?.code}
                placeholder="Select qualification"
                items={qualifications}
                disabled={!selectedBoard}
                onSelect={(q) => {
                  setSelectedQualification(q);
                  setSelectedSubject(null);
                  setSubjects([]);
                }}
              />
              <Field
                label="Subject"
                value={selectedSubject?.code}
                placeholder="Select subject"
                items={subjects}
                disabled={!selectedQualification}
                onSelect={(s) => setSelectedSubject(s)}
              />
            </Flex>

            <Flex justifyContent="flex-end" mt="8px">
              <Button
                variant="unstyled"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                gap="8px"
                px="22px"
                height="42px"
                borderRadius="full"
                bg={p.accent}
                color={p.onAccent}
                fontSize="14px"
                fontWeight="600"
                isDisabled={!canCreate}
                opacity={canCreate ? 1 : 0.5}
                cursor={canCreate ? "pointer" : "not-allowed"}
                _hover={canCreate ? { bg: p.accentHover } : {}}
                onClick={handleCreate}
              >
                {submitting && <Spinner size="sm" />}
                {submitting ? "Creating…" : "Create space"}
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateClassroom;
